import { FC, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "src/utils/utils";

interface DatePickerProps {
  /** ISO string or YYYY-MM-DD. Empty string when no value. */
  value?: string;
  /** Called with ISO string (UTC midnight) or "" when cleared. */
  onChange: (isoString: string) => void;
  /** Min date (YYYY-MM-DD). */
  min?: string;
  /** Max date (YYYY-MM-DD). */
  max?: string;
  /** Date the calendar opens to when `value` is empty (YYYY-MM-DD). Defaults to today. */
  defaultOpenDate?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  containerClassName?: string;
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/** Format a Date as dd-mm-yyyy (from its UTC parts so display stays locale-stable). */
const formatDDMMYYYY = (iso: string): string => {
  if (!iso) return "";
  const datePart = iso.split("T")[0];
  const [y, m, d] = datePart.split("-");
  if (!y || !m || !d) return "";
  return `${d}-${m}-${y}`;
};

/** Parse "YYYY-MM-DD" to { y, m, d } (month is 1-12). */
const parseYMD = (s?: string): { y: number; m: number; d: number } | null => {
  if (!s) return null;
  const datePart = s.split("T")[0];
  const parts = datePart.split("-").map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  return { y: parts[0], m: parts[1], d: parts[2] };
};

const daysInMonth = (y: number, m: number) => new Date(y, m, 0).getDate();

const DatePicker: FC<DatePickerProps> = ({
  value,
  onChange,
  min,
  max,
  defaultOpenDate,
  placeholder = "dd-mm-yyyy",
  disabled = false,
  className,
  containerClassName,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Decide which month/year the calendar is viewing.
  const initialView = useMemo(() => {
    const fromValue = parseYMD(value);
    if (fromValue) return { y: fromValue.y, m: fromValue.m };
    const fromDefault = parseYMD(defaultOpenDate);
    if (fromDefault) return { y: fromDefault.y, m: fromDefault.m };
    const today = new Date();
    return { y: today.getFullYear(), m: today.getMonth() + 1 };
  }, [value, defaultOpenDate]);

  const [viewYear, setViewYear] = useState(initialView.y);
  const [viewMonth, setViewMonth] = useState(initialView.m);

  // When opening, re-sync view to value/default.
  useEffect(() => {
    if (open) {
      setViewYear(initialView.y);
      setViewMonth(initialView.m);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const minYMD = parseYMD(min);
  const maxYMD = parseYMD(max);

  const isDisabled = (y: number, m: number, d: number): boolean => {
    const cmp = y * 10000 + m * 100 + d;
    if (minYMD) {
      const v = minYMD.y * 10000 + minYMD.m * 100 + minYMD.d;
      if (cmp < v) return true;
    }
    if (maxYMD) {
      const v = maxYMD.y * 10000 + maxYMD.m * 100 + maxYMD.d;
      if (cmp > v) return true;
    }
    return false;
  };

  const selected = parseYMD(value);
  const today = new Date();
  const todayYMD = { y: today.getFullYear(), m: today.getMonth() + 1, d: today.getDate() };

  const goPrevMonth = () => {
    if (viewMonth === 1) {
      setViewMonth(12);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };
  const goNextMonth = () => {
    if (viewMonth === 12) {
      setViewMonth(1);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const handleSelect = (d: number) => {
    if (isDisabled(viewYear, viewMonth, d)) return;
    const iso = new Date(Date.UTC(viewYear, viewMonth - 1, d, 0, 0, 0, 0)).toISOString();
    onChange(iso);
    setOpen(false);
  };

  const handleClear = () => {
    onChange("");
    setOpen(false);
  };

  // Build calendar grid.
  const firstWeekday = new Date(viewYear, viewMonth - 1, 1).getDay(); // 0 = Sun
  const totalDays = daysInMonth(viewYear, viewMonth);
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  // Year range for the year dropdown.
  const yearRangeStart = (minYMD?.y ?? viewYear - 50);
  const yearRangeEnd = (maxYMD?.y ?? viewYear + 10);
  const years: number[] = [];
  for (let y = yearRangeStart; y <= yearRangeEnd; y++) years.push(y);

  return (
    <div ref={wrapperRef} className={cn("relative w-full", containerClassName)}>
      <div
        className={cn(
          "inline-flex border border-[#e0e0e0] rounded-lg w-full items-center justify-between px-3 py-1 bg-white",
          disabled ? "bg-gray-100 cursor-not-allowed" : "cursor-pointer",
          className
        )}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span className={cn("text-sm", !value && "text-gray-400")}>
          {value ? formatDDMMYYYY(value) : placeholder}
        </span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-500">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      </div>

      {open && !disabled && (
        <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-[260px]">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={goPrevMonth}
              className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded"
            >
              ‹
            </button>
            <div className="flex items-center gap-1">
              <select
                value={viewMonth}
                onChange={(e) => setViewMonth(Number(e.target.value))}
                className="text-sm border border-gray-200 rounded px-1 py-0.5 outline-none"
              >
                {MONTHS.map((name, idx) => (
                  <option key={name} value={idx + 1}>{name}</option>
                ))}
              </select>
              <select
                value={viewYear}
                onChange={(e) => setViewYear(Number(e.target.value))}
                className="text-sm border border-gray-200 rounded px-1 py-0.5 outline-none"
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={goNextMonth}
              className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded"
            >
              ›
            </button>
          </div>

          {/* Weekday row */}
          <div className="grid grid-cols-7 text-xs text-gray-500 mb-1">
            {WEEKDAYS.map((wd) => (
              <div key={wd} className="text-center py-1">{wd}</div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d, i) => {
              if (d === null) {
                return <div key={i} />;
              }
              const disabledCell = isDisabled(viewYear, viewMonth, d);
              const isSelected =
                selected &&
                selected.y === viewYear &&
                selected.m === viewMonth &&
                selected.d === d;
              const isToday =
                todayYMD.y === viewYear &&
                todayYMD.m === viewMonth &&
                todayYMD.d === d;
              return (
                <button
                  key={i}
                  type="button"
                  disabled={disabledCell}
                  onClick={() => handleSelect(d)}
                  className={cn(
                    "text-sm py-1 rounded text-center",
                    disabledCell && "text-gray-300 cursor-not-allowed",
                    !disabledCell && !isSelected && "hover:bg-gray-100",
                    isSelected && "bg-purple-600 text-white",
                    !isSelected && isToday && "border border-purple-400"
                  )}
                >
                  {d}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
